"use client";

import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function ContactFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const {
    ref: cardRef,
    isVisible: cardVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.3
  });
  const {
    ref: field1Ref,
    isVisible: field1Visible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.5
  });
  const {
    ref: field2Ref,
    isVisible: field2Visible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.5
  });
  const {
    ref: field3Ref,
    isVisible: field3Visible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.5
  });
  const {
    ref: buttonRef,
    isVisible: buttonVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.5
  });
  if (isSubmitted && !submitError) {
    return <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <div className="w-full max-w-md bg-primary/30 backdrop-blur-sm rounded-lg shadow-soft p-8 text-center">
          <p className="text-foreground text-lg font-semibold mb-4">
            {successMessage}
          </p>
          <Button onClick={resetForm} variant="outline" className="mt-2">
            Send Another Message
          </Button>
        </div>
      </div>;
  }
  return <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <div ref={cardRef} className={`w-full max-w-md bg-primary-dark/30 backdrop-blur-sm rounded-lg shadow-soft p-8 transition-all duration-[var(--duration-slow)] ease-[var(--ease-smooth)] ${cardVisible ? "opacity-100 translate-y-0 animate-pulse" : "opacity-0 translate-y-4"}`} style={{
      animationIterationCount: 1,
      animationDuration: "600ms"
    }}>
        <h2 className="text-2xl font-display font-bold text-foreground mb-6 text-center">
          Get in Touch
        </h2>
        <p className="text-muted-foreground text-sm mb-8 text-center">
          Have questions? We'd love to hear from you.
        </p>

        <Form onSubmit={handleSubmit}>
          <div className="space-y-6">
            <div ref={field1Ref} className={`transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] ${field1Visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
            transitionDelay: "100ms"
          }}>
              <FormField control={control} name="name" rules={{
              required: "Name is required",
              minLength: {
                value: 2,
                message: "Name must be at least 2 characters"
              }
            }} render={({
              field
            }) => <FormItem>
                    <FormLabel className="text-foreground font-semibold">Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Your full name" {...field} className="border-b-2 border-muted focus-visible:border-primary transition-colors duration-[var(--duration-fast)] bg-transparent rounded-none shadow-none px-0 py-2" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
            </div>

            <div ref={field2Ref} className={`transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] ${field2Visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
            transitionDelay: "200ms"
          }}>
              <FormField control={control} name="email" rules={{
              required: "Email is required",
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: "Please enter a valid email address"
              }
            }} render={({
              field
            }) => <FormItem>
                    <FormLabel className="text-foreground font-semibold">Email</FormLabel>
                    <FormControl>
                      <Input type="email" placeholder="you@example.com" {...field} className="border-b-2 border-muted focus-visible:border-primary transition-colors duration-[var(--duration-fast)] bg-transparent rounded-none shadow-none px-0 py-2" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
            </div>

            <div ref={field3Ref} className={`transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] ${field3Visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
            transitionDelay: "300ms"
          }}>
              <FormField control={control} name="message" rules={{
              required: "Message is required",
              minLength: {
                value: 10,
                message: "Message must be at least 10 characters"
              }
            }} render={({
              field
            }) => <FormItem>
                    <FormLabel className="text-foreground font-semibold">Message</FormLabel>
                    <FormControl>
                      <textarea placeholder="Tell us what's on your mind" {...field} rows={4} className="w-full border-b-2 border-muted focus-visible:border-primary focus-visible:ring-ring/50 focus-visible:ring-[3px] focus-visible:outline-none transition-all duration-[var(--duration-fast)] bg-transparent rounded-none shadow-none px-0 py-2 resize-none text-base text-foreground placeholder:text-muted-foreground" />
                    </FormControl>
                    <FormDescription className="text-muted-foreground text-xs mt-1">
                      Minimum 10 characters
                    </FormDescription>
                    <FormMessage />
                  </FormItem>} />
            </div>

            {submitError && <div className="p-4 bg-destructive/10 border border-destructive rounded-md">
                <p className="text-destructive text-sm">{submitError}</p>
              </div>}

            <div ref={buttonRef} className={`transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] ${buttonVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
            transitionDelay: "400ms"
          }}>
              <Button type="submit" disabled={isSubmitting} className="w-full bg-primary text-foreground-primary hover:bg-primary-hover hover:-translate-y-1 hover:shadow-medium active:translate-y-0 transition-all duration-[var(--duration-medium)] ease-[var(--ease-spring)] font-semibold py-3">
                {isSubmitting ? <>
                    <span className="inline-block animate-spin mr-2">⏳</span>
                    Sending...
                  </> : "Send Message"}
              </Button>
            </div>
          </div>
        </Form>
      </div>
    </div>;
}
export default function ContactForm() {
  return <FormProvider className="w-full" formId="contact-form-hero" sectionName="contact-section" defaultValues={{
    name: "",
    email: "",
    message: ""
  }} validationRules={{
    name: {
      required: true,
      minLength: 2,
      maxLength: 50
    },
    email: {
      required: true,
      pattern: "email"
    },
    message: {
      required: true,
      minLength: 10
    }
  }} submitText="Send Message" successMessage="Thank you! We'll get back to you soon.">
      <ContactFormContent />
    </FormProvider>;
}